import * as React from 'react';

function getLineHeightPx(el: HTMLElement): number {
  const style = getComputedStyle(el);
  const lineHeight = parseFloat(style.lineHeight);
  if (!Number.isNaN(lineHeight)) return lineHeight;
  const fontSize = parseFloat(style.fontSize) || 16;
  return fontSize * 1.25;
}

const NOTE_CONTENT_MAX_LENGTH = 1000;

interface AutoResizeTextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'maxLength'> {
  className?: string;
  /** Максимум символов (по умолчанию 1000 для заметок). */
  maxLength?: number;
}

/** Текстовое поле с авто-увеличением высоты: минимум 1 строка, без максимума по высоте, без ручного ресайза. */
export const AutoResizeTextarea = React.forwardRef<HTMLTextAreaElement, AutoResizeTextareaProps>(
  function AutoResizeTextarea(
    { className = '', value, onChange, maxLength = NOTE_CONTENT_MAX_LENGTH, spellCheck = false, ...props },
    ref
  ) {
    const textareaRef = React.useRef<HTMLTextAreaElement>(null);
    const resolvedRef = (node: HTMLTextAreaElement | null) => {
      (textareaRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
      if (typeof ref === 'function') ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLTextAreaElement | null>).current = node;
    };

    React.useLayoutEffect(() => {
      const el = textareaRef.current;
      if (!el) return;
      const style = getComputedStyle(el);
      const lineH = getLineHeightPx(el);
      const paddingTop = parseFloat(style.paddingTop) || 0;
      const paddingBottom = parseFloat(style.paddingBottom) || 0;
      const minH = paddingTop + paddingBottom + lineH;

      el.style.minHeight = '0';
      el.style.height = '0';
      const scrollH = el.scrollHeight;
      el.style.height = `${Math.max(scrollH, minH)}px`;
      el.style.minHeight = `${minH}px`;
      el.style.overflowY = scrollH > 400 ? 'auto' : 'hidden';
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      if (maxLength != null && e.target.value.length > maxLength) return;
      onChange?.(e);
    };

    return (
      <textarea
        ref={resolvedRef}
        className={className}
        value={value}
        onChange={handleChange}
        maxLength={maxLength}
        spellCheck={spellCheck}
        style={{ resize: 'none', overflow: 'hidden' }}
        {...props}
      />
    );
  }
);

export { NOTE_CONTENT_MAX_LENGTH };
